import type { Slide } from "../components/sortable-slide";
import type { GenerateOutlineResult } from "../types/generation";

// In-memory draft of the describe step (prompt, choices, generated/edited
// outline) so navigating away within the funnel — e.g. to ai/generating and
// back — restores the step instead of resetting it. Session-scoped; holds
// File objects, so sessionStorage is not an option.
export interface DescribeDraft {
  /**
   * The template the draft's rows belong to: its content pointer, or a
   * stand-in for slides picked in the library or no template. A draft is
   * whole for the same template; for another, only the user's own words and
   * choices survive -- see readDescribeDraft.
   */
  templateKey: string;
  prompt: string;
  length: string;
  language: string;
  tone: string;
  files: File[];
  slides: Slide[];
  phase: "compose" | "outline";
  outlineMeta: GenerateOutlineResult | null;
  designSystemId?: string | null;
  /** "Keep original assets" on a template path; see describe.tsx. */
  keepOriginalAssets?: boolean;
}

let draft: DescribeDraft | null = null;

export const saveDescribeDraft = (next: DescribeDraft) => {
  draft = next;
};

/**
 * The draft for this template. For a different one, the rows, the outline
 * written on them and the template-bound choices are dropped: the user went
 * back and picked another template, and its list is that template's. What
 * they typed and picked for themselves -- the prompt, its file, length,
 * language, tone -- carries over.
 */
export const readDescribeDraft = (
  templateKey: string,
): DescribeDraft | null => {
  if (!draft) return null;
  if (draft.templateKey === templateKey) return draft;
  return {
    ...draft,
    templateKey,
    slides: [],
    phase: "compose",
    outlineMeta: null,
    designSystemId: undefined,
    keepOriginalAssets: undefined,
  };
};

/** Called once a story is created — the draft has served its purpose. */
export const clearDescribeDraft = () => {
  draft = null;
};
